import React from 'react';
import { PRODUCTS, PROBLEMS, ACCENT, VERDICTS, FUSION, SPEC_ROWS, STAGES, CHECKS, BELT } from './data.js';
import { Link, Shot, ContactButton, accentVars, onAccent, palette, scrollToId } from './ui.jsx';

export function ProductPage({ productKey }) {
  const p = PRODUCTS.find(x => x.key === productKey);
  const num = '0' + (PRODUCTS.indexOf(p) + 1);
  const pal = palette(p.key);
  const tone = (j) => ({ background: pal[j % 3], color: onAccent(pal[j % 3]) });

  return (
    <div style={accentVars(p.key)}>
      <section className="band-dk p-hero">
        <div className="glow" />
        <div className="dots" />
        <div className="wrap inner">
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span>/</span><span>Products</span><span>/</span><span aria-current="page">{p.name}</span>
          </nav>
          <div className="grid g2 cols">
            <div className="copy">
              <div className="p-badge"><i /><div>{`${num} · ${p.kind}`}</div></div>
              <h1>{p.name}</h1>
              <div className="tag">{p.tagline}</div>
              <p className="desc">{p.desc}</p>
              <div className="ctas">
                <ContactButton icon="event">Book a demo</ContactButton>
                <a href="#ps" className="btn btn-ghost" onClick={(e) => { e.preventDefault(); scrollToId('ps'); }}>What it solves</a>
              </div>
            </div>
            <div className="window">
              <div className="bar">
                <i style={{ background: '#FF5C6C' }} /><i style={{ background: '#FFB547' }} /><i style={{ background: '#2ED3A0' }} />
                <span>{p.url}</span>
              </div>
              <div className="view"><Shot src={p.src1} alt={p.shot1} /></div>
            </div>
          </div>
        </div>
      </section>

      <div className="wrap grid g4 specs">
        {p.specs.map(x => (
          <div className="spec" key={x.l}><div className="v">{x.v}</div><div className="l">{x.l}</div></div>
        ))}
      </div>

      <section id="ps" className="wrap sec" style={{ paddingBottom: 64 }}>
        <div className="eyebrow">PROBLEM → SOLUTION</div>
        <h2 className="h2">What it fixes on your line.</h2>
        <div className="ps">
          {PROBLEMS[p.key].map(x => (
            <div className="ps-row" key={x.f}>
              <div className="ps-p"><div className="tag-chip problem">PROBLEM</div><div>{x.p}</div></div>
              <div className="arrow" aria-hidden="true">→</div>
              <div className="ps-s">
                <div className="head"><div className="tag-chip solution">SOLUTION</div><div>{x.f}</div></div>
                <div className="body">{x.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="band-2">
        <div className="wrap sec">
          <div className="eyebrow">HOW IT WORKS</div>
          <h2 className="h2">{p.howTitle}</h2>
          <div className="grid g3 how">
            {p.how.map((h, j) => (
              <div className="how-card" key={h.n} style={tone(j)}>
                <div className="top"><div>{h.n}</div><div>{h.tag}</div></div>
                <div className="t">{h.t}</div>
                <div className="d">{h.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {p.key === 'edge' && <EdgeExtras />}
      {p.key === 'detectorlab' && <PipelineSection />}
      {p.key === 'visionlab' && <ReleaseChecks />}
      {p.key === 'simulator' && <SimulatorControls />}
      {p.key === 'cloud' && (
        <section className="wrap" style={{ paddingTop: 80 }}>
          <div className="early">
            <div><div className="mono" style={{ fontSize: 12, letterSpacing: '.08em' }}>UNDER DEVELOPMENT</div><div className="t">Want early access to VIAI Cloud?</div></div>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToId('contact'); }}>Join the early-access list →</a>
          </div>
        </section>
      )}

      <section className="wrap sec">
        <div className="eyebrow">FEATURES</div>
        <h2 className="h2">What you get.</h2>
        <div className="grid g3 feat">
          {p.features.map((x, j) => (
            <div className="feat-card" key={x.t}>
              <div className="n" style={tone(j)}>{`0${j + 1}`}</div>
              <div className="t">{x.t}</div>
              <div className="d">{x.d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="band-2">
        <div className="wrap sec">
          <div className="eyebrow">SCREENSHOTS</div>
          <h2 className="h2">See it in use.</h2>
          <div className="grid g2" style={{ gap: 24 }}>
            {[[p.src2, p.shot2], [p.src3, p.shot3]].map(([src, cap], i) => (
              <figure className="fig" key={cap} style={{ margin: 0 }}>
                <div className="frame"><Shot src={src} alt={cap} /></div>
                <figcaption className="cap"><span>{`FIG. 0${i + 1}`}</span><div>{cap}</div></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap sec">
        <div className="eyebrow">USE CASES</div>
        <h2 className="h2">Where plants use it.</h2>
        <div className="grid g3" style={{ gap: 16 }}>
          {p.uses.map((u, j) => (
            <div className="use-card" key={u.t}>
              <div className="who" style={tone(j)}>{u.who}</div>
              <div className="t">{u.t}</div>
              <div className="d">{u.d}</div>
            </div>
          ))}
        </div>
        <div className="related">
          <div className="lbl">WORKS WITH</div>
          {p.rel.map(k => (
            <Link to={'/product/' + k} className="rel-chip" key={k} style={{ '--pac': ACCENT[k] }}>
              <i /><div>{`${PRODUCTS.find(x => x.key === k).name} →`}</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function EdgeExtras() {
  return (
    <>
      <section className="band-dk">
        <div className="wrap sec">
          <div className="eyebrow">DECISION LOGIC</div>
          <h2 className="h2">Many defects. One clear call.</h2>
          <div className="grid g3" style={{ gap: 16 }}>
            {VERDICTS.map(v => (
              <div className="verdict" key={v.t} style={{ '--vc': v.c }}>
                <div className="chip">{v.t}</div>
                <div className="a">{v.a}</div>
                <div className="d">{v.d}</div>
              </div>
            ))}
          </div>
          <div className="grid g2 fusion">
            <div className="fusion-list">
              <div style={{ color: 'var(--mutedDk)', fontSize: 11, letterSpacing: '.08em' }}>DETECTED ON THIS UNIT</div>
              {FUSION.map(x => (
                <div className="fusion-row" key={x.sev}>
                  <div style={{ color: x.c }}>{x.sev}</div>
                  <div><div>{x.d}</div><div className="fusion-bar"><i style={{ width: Math.round(parseFloat(x.s) * 100) + '%', background: x.c }} /></div></div>
                  <div style={{ textAlign: 'right', color: 'var(--sub)' }}>{x.s}</div>
                </div>
              ))}
            </div>
            <div className="fusion-out">
              <div className="mono" style={{ fontSize: 11, letterSpacing: '.08em', opacity: .9 }}>SEVERITY FUSION · FINAL DECISION</div>
              <div className="big">REJECT</div>
              <div style={{ fontSize: 16 }}>Fused 0.94. Critical defect, so reject regardless.</div>
            </div>
          </div>
        </div>
      </section>
      <section className="wrap sec" style={{ paddingBottom: 40 }}>
        <div className="eyebrow">SPECIFICATIONS</div>
        <h2 className="h2">Specifications, not slogans.</h2>
        <div className="spec-table">
          {SPEC_ROWS.map(r => (
            <div className="spec-row" key={r.k}><div className="k">{r.k}</div><div className="v">{r.v}</div></div>
          ))}
        </div>
      </section>
    </>
  );
}

function PipelineSection() {
  return (
    <section className="band-dk">
      <div className="wrap sec">
        <div className="eyebrow">THE PIPELINE</div>
        <h2 className="h2">13 stages. Every one inspectable.</h2>
        <ol className="stages" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {STAGES.map((t, i) => (
            <li className="stage" key={t} style={{ background: `rgba(139,124,255,${(0.1 + i * 0.03).toFixed(2)})` }}>
              <span>{String(i + 1).padStart(2, '0')}</span>{t}
            </li>
          ))}
        </ol>
        <div style={{ marginTop: 24, fontSize: 16, color: 'var(--sub)' }}>Each stage shows its result and its timing.</div>
      </div>
    </section>
  );
}

function ReleaseChecks() {
  return (
    <section className="band-dk">
      <div className="wrap sec">
        <div className="eyebrow">RELEASE CHECKS</div>
        <h2 className="h2">Nothing reaches the line unchecked.</h2>
        <div className="grid g4" style={{ gap: 16 }}>
          {CHECKS.map(x => (
            <div className="check" key={x.t}>
              <div className="mark ok" aria-hidden="true">✓</div>
              <div className="t">{x.t}</div>
              <div className="d">{x.d}</div>
            </div>
          ))}
        </div>
        <div className="pills">
          <div className="mono" style={{ fontSize: 12, color: 'var(--mutedDk)', marginRight: 6 }}>MODELS</div>
          <div className="pill" style={{ background: 'var(--pass)' }}>PatchCore</div>
          <div className="pill" style={{ background: 'var(--pass)' }}>EfficientAD</div>
          <div className="pill" style={{ background: 'var(--violet)' }}>EfficientNet-B0 classifier</div>
        </div>
      </div>
    </section>
  );
}

// A static illustration of the Simulator's controls, not a working widget.
function SimulatorControls() {
  return (
    <section className="band-dk">
      <div className="wrap sec grid g2 sim">
        <div>
          <div className="eyebrow">DRY-RUN CONTROLS</div>
          <h2 className="h2" style={{ marginBottom: 20 }}>A production line on your desk.</h2>
          <div style={{ fontSize: 18, lineHeight: 1.55, color: 'var(--sub)', maxWidth: 480 }}>Set the conditions, run the belt, and measure what was caught.</div>
        </div>
        <div className="sim-panel" aria-hidden="true">
          <div><div className="k"><div>LINE SPEED</div></div>
            <div className="sim-seg"><div>Slow</div><div className="on">Medium</div><div>Fast</div></div></div>
          <div><div className="k"><div>DEFECT RATE</div><div style={{ color: 'var(--review)' }}>5%</div></div>
            <div className="sim-slider"><i /><b /></div></div>
          <div><div className="k"><div>MODE</div></div>
            <div className="sim-modes"><div className="on">Random</div><div>Bursts</div><div>Per defect type</div></div></div>
          <div className="belt">{BELT.map((c, i) => <i key={i} style={{ background: c }} />)}</div>
        </div>
      </div>
    </section>
  );
}
